import { ChartViewByOption } from '@libs/api';
import { IChartSeriesOption } from '@portal/app/charts/shared/chart-model';
import { SelectItem, SelectItemGroup } from 'primeng/api';

export interface MetricGroup<T> {
  label: string;
  value: string;
  items: T[];
}

export interface NativeDataSetMetricOption {
  label: string;
  value: string;
  disabled: boolean;
}

// Define specific types using the generic interface
export type NativeDataSetMetricGroup = MetricGroup<NativeDataSetMetricOption>;
export type NativeChartMetricGroup = MetricGroup<IChartSeriesOption>;
export type ChartViewByOptionGroup = MetricGroup<ChartViewByOption>;

export class MetricsGroupOptions {
  private static DEFAULT_GROUPS = [
    {
      label: 'Incremental Metrics',
      literalIds: [
        'adjustmentFactor',
        'ordersI',
        'salesI',
        'percInc',
        'cpoI',
        'roasI',
        'percOrdersI',
        'percSalesI',
        'netProfitIPerDollar',
        'percSalesISelected'
      ]
    },
    {
      label: 'Platform Metrics',
      literalIds: [
        'mediaSpend',
        'totalSpend',
        'percTotalSpend',
        'ordersLT',
        'salesLT',
        'cpoLT',
        'roasLT',
        'cpm',
        'cpc',
        'impressionConversionRate',
        'clickConversionRate',
        'spend',
        'impressions',
        'clicks'
      ]
    },
    {
      label: 'Business Metrics',
      literalIds: [
        'totalOrders',
        'totalSales',
        'baselineOrders',
        'baselineSales'
      ]
    },
    {
      label: 'Others',
      literalIds: []
    }
  ];

  /**
   * Creates empty group structures based on DEFAULT_GROUPS
   * @returns Array of empty groups ready to be populated
   */
  private static createEmptyGroups<
    T extends { label?: string },
    R extends { label: string; value: string; items: T[] }
  >(): R[] {
    // Create a copy of DEFAULT_GROUPS as our result structure
    return this.DEFAULT_GROUPS.map(
      (group) =>
        ({
          label: group.label,
          value: group.label,
          items: []
        }) as unknown as R
    );
  }

  /** * Sorts items within each group by label (alphabetically)
   * @param groups Array of groups with items that have label property
   */
  private static sortItems<
    T extends { label?: string },
    G extends { items: T[] }
  >(groups: G[]) {
    groups.forEach((group) => {
      group.items.sort((a, b) => {
        const aLabel = a.label || '';
        const bLabel = b.label || '';
        return aLabel.localeCompare(bLabel);
      });
    });
  }

  /**
   * Private helper method to organize options into their respective groups
   * @param options The array of options to be organized
   * @param getMatchValue Function to extract the value to match against literalIds
   * @returns An array of grouped items
   */
  private static organizeOptionsIntoGroups<
    T extends { label?: string },
    R extends { label: string; value?: string; items: T[] }
  >(options: T[], getMatchValue: (option: T) => string | undefined): R[] {
    // Create a copy of DEFAULT_GROUPS as our result structure
    const groups = this.createEmptyGroups();

    // Find "Others" group for items that don't match any specific group
    const othersGroup = groups.find((g) => g.label === 'Others');

    // Process each option
    options.forEach((option) => {
      let matched = false;
      const matchValue = getMatchValue(option);

      if (matchValue) {
        // Check each group (except "Others") to see if the option belongs there
        for (const group of groups) {
          if (group.label === 'Others') continue;

          // Find the original group definition to access literalIds
          const defaultGroup = this.DEFAULT_GROUPS.find(
            (dg) => dg.label === group.label
          );

          if (defaultGroup && defaultGroup.literalIds.includes(matchValue)) {
            group.items.push(option);
            matched = true;
            break;
          }
        }
      }

      // If option didn't match any group, add to "Others"
      if (!matched && othersGroup) {
        othersGroup.items.push(option);
      }
    });

    // Sort items within each group by label (alphabetically)
    this.sortItems(groups);

    // Return only groups that have items
    return groups.filter((group) => group.items.length > 0) as R[];
  }

  /**
   * Groups NativeDataSetMetricOption items into metric categories
   * @param options Array of NativeDataSetMetricOption to be organized
   * @returns An array of grouped NativeDataSetMetricOption items
   */
  public static getNativeDataSetOptions(
    options: NativeDataSetMetricOption[]
  ): NativeDataSetMetricGroup[] {
    return this.organizeOptionsIntoGroups<
      NativeDataSetMetricOption,
      NativeDataSetMetricGroup
    >(options, (option) => option.value);
  }

  /**
   * Groups IChartSeriesOption items into metric categories
   * @param options Array of IChartSeriesOption to be organized
   * @returns An array of grouped IChartSeriesOption items
   */
  public static getNativeChartOptions(
    options: IChartSeriesOption[]
  ): NativeChartMetricGroup[] {
    return this.organizeOptionsIntoGroups<
      IChartSeriesOption,
      NativeChartMetricGroup
    >(options, (option) =>
      option.fields && option.fields.length > 0 ? option.fields[0] : undefined
    );
  }

  /**
   * Groups ChartViewByOption items into metric categories
   * @param options Array of ChartViewByOption to be organized
   * @returns An array of grouped ChartViewByOption items
   */
  public static getChartViewByOptions(
    options: ChartViewByOption[]
  ): ChartViewByOptionGroup[] {
    return this.organizeOptionsIntoGroups<
      ChartViewByOption,
      ChartViewByOptionGroup
    >(options, (option) => option.value);
  }

  /**
   * Groups SelectItem options into metric categories
   * @param options Array of SelectItem to be organized
   * @returns An array of grouped SelectItem
   */
  public static getSelectItemGroups(options: SelectItem[]): SelectItemGroup[] {
    return this.organizeOptionsIntoGroups<SelectItem, SelectItemGroup>(
      options,
      (option) => option.value
    );
  }
}
